import React from 'react';
import { useTranslation } from 'react-i18next';
import ClubLogo from '../ClubLogo';
import { useDateFormat } from '../../hooks/useDateFormat';
import { Match, Team } from '../../types/api';

interface PublicPageHeaderProps {
  team: Team | null;
  match: Match | null;
  /** Naast de titel, zoals de live-badge. */
  badge?: React.ReactNode;
  /** Verzamel- en aanvangstijd rechts; op de volgpagina van het live verslag overbodig. */
  showTimes?: boolean;
}

/**
 * De blauwe kop van de publieke pagina's: clublogo, "team vs tegenstander" en de datum.
 * Gedeeld door de opstelling en de volgpagina van het live verslag, zodat een ouder die van
 * de een naar de ander tikt op dezelfde site blijft.
 */
export const PublicPageHeader: React.FC<PublicPageHeaderProps> = ({ team, match, badge, showTimes = true }) => {
  const { t } = useTranslation('playerView');
  const { formatDate } = useDateFormat();
  const formatTimeOnly = (value: string | null) => formatDate(value, 'time');

  return (
    <div className="bg-hockey-blue text-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ClubLogo
            logoUrl={team?.club?.logoUrl}
            altText={team?.club?.name || t('header.fallbackClub')}
            width="40px"
          />
          <div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h1 className="text-xl font-bold">
                {match
                  ? t('header.matchTitle', {
                      team: team?.shortName || t('header.fallbackTeam'),
                      opponent: match.opponent,
                    })
                  : team?.name || t('header.fallbackClub')}
              </h1>
              {badge}
            </div>
            <p className="text-sm opacity-90">
              {match && match.startTime
                ? match.field
                  ? t('header.dateWithField', {
                      date: formatDate(match.startTime, 'abbreviated'),
                      field: match.field,
                    })
                  : formatDate(match.startTime, 'abbreviated')
                : t('header.noMatchSelected')}
            </p>
          </div>
        </div>
        {showTimes && match && (
          <div className="text-right text-sm">
            {match.meetTime && <p>{t('header.meetTime', { time: formatTimeOnly(match.meetTime) })}</p>}
            {match.startTime && <p>{t('header.startTime', { time: formatTimeOnly(match.startTime) })}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
